import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { arrayRange } from '@/utils/array.utils'
import { format } from '@/utils/date.utils'
import Insured from '@/modules/common/common/models/APP/Insured'
import type { FormAnswersType } from '@/modules/core/models/types'
import type AdditionalCoverageConfig from '@/modules/health/hiring/models/APP/AdditionalCoverageConfig'
import { addCommas } from '@/utils/textUtils'
import { CURRENCY } from '@/modules/common/common/constants/CommonConstants'

export function useAdditionalCoveragesComposable(answers: FormAnswersType) {
  const { t } = useI18n()

  const insureds = ref<Insured[]>(answers.insureds)

  function getFooterMessage(key: string, min: number, max: number, step: number) {
    const range = arrayRange(min, max - step, step).join(', ')
    const values = [range, max].map((v) => addCommas(v))

    return t(`hiring.common.additional-coverages.coverage-footer`, values)
  }

  function filterCoveragesByBirthDate(
    coveragesConfig: AdditionalCoverageConfig[],
    birthDate: Date
  ) {
    if (!birthDate || isNaN(birthDate.getTime())) return coveragesConfig

    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const m = today.getMonth() - birthDate.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }

    return coveragesConfig.filter((coverage) => {
      return age >= coverage.minAge && age <= coverage.maxAge
    })
  }

  function loadAdditionalCoverages(coveragesConfig: AdditionalCoverageConfig[]) {
    const hideIndex = insureds.value.length === 1 && insureds.value[0].isHolder
    return insureds.value.map((insured, i) => {
      const i18nLabel = insured.isHolder ? 'holder-insured' : 'insured'
      const index = hideIndex ? null : i + 1
      const title = t(`hiring.common.${i18nLabel}`, index as any)
      const labelDate = format(insured.userDetails.birthDate)
      const coverageValues = insured.additionalCoverages

      const availableCoverages = filterCoveragesByBirthDate(
        coveragesConfig,
        insured.userDetails.birthDate as Date
      )
      const options = availableCoverages.map((coverage) => {
        const coverageOption = coverageValues.find((c) => c.key === coverage.code)

        return {
          ...coverage,
          currency: CURRENCY,
          checked: coverageOption?.checked ?? false,
          value: coverageOption?.value ?? coverage.defaultValue,
          footerMessage: getFooterMessage(
            coverage.code,
            Number(coverage.min),
            Number(coverage.max),
            Number(coverage.step)
          )
        }
      })

      return { title, date: labelDate, options }
    })
  }

  return { loadAdditionalCoverages }
}
